import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { Project } from 'src/app/core/objects/project';
import { ProjectService } from 'src/app/core/services/project.service';
import { Position } from 'src/app/core/types';
import { FormDataType } from '../../add-load.component';
import { Subscription } from 'rxjs';

@Component({
    selector: 'gl-load-gravity-of-center',
    templateUrl: './load-gravity-of-center.component.html',
    styleUrls: ['./load-gravity-of-center.component.scss'],
    standalone: false
})
export class LoadGravityOfCenterComponent implements OnInit, OnChanges, OnDestroy {
  @Input() public loadData: FormDataType;
  @Input() public defaultValue: Position;
  @Output() public centerOfMass = new EventEmitter<Position>();
  public loadDataVis = null;
  public gravityShowCenter: boolean;
  public form: UntypedFormGroup;
  public project: Project;
  public unit: string;
  public min = 0;
  public maxWidth = 100;
  public maxHeight = 100;
  public maxLength = 100;
  public hasDimensions: boolean;
  private subscription: Subscription;

  constructor(public projectService: ProjectService) {
    this.projectService.project.subscribe((project) => {
      this.project = project;
      this.unit = project.options.unit;
    });
    this.form = this.generateForm();
    this.form.disable();
    this.subscription = this.form.valueChanges.subscribe(() => {
      this.onSubmit();
    });
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  public ngOnInit(): void {
    this.gravityShowCenter = !!this.defaultValue;
    this.loadDataVis = { ...this.loadData };
    if (this.defaultValue) {
      this.form.controls.axisX.setValue(this.defaultValue.x, { emitEvent: false });
      this.form.controls.axisY.setValue(this.defaultValue.y, { emitEvent: false });
      this.form.controls.axisZ.setValue(this.defaultValue.z, { emitEvent: false });
      this.setGravity(true);
    } else {
      this.gravityShowCenter = false;
    }
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.loadData?.currentValue) {
      let isChanged: boolean;
      if (this.loadData.loadType === 0) {
        this.hasDimensions = !!(
          this.loadData?.width &&
          this.loadData?.height &&
          this.loadData?.length
        );
        isChanged =
          this.maxWidth !== this.loadData?.width ||
          this.maxHeight !== this.loadData?.height ||
          this.maxLength !== this.loadData?.length;
        this.maxWidth = this.loadData?.width ? this.loadData.width : 100;
        this.maxHeight = this.loadData?.height ? this.loadData.height : 100;
        this.maxLength = this.loadData?.length ? this.loadData.length : 100;
      } else if (this.loadData.loadType === 1) {
        this.hasDimensions = !!(this.loadData?.diameter && this.loadData?.height);
        isChanged =
          this.maxWidth !== this.loadData?.diameter ||
          this.maxHeight !== this.loadData?.height ||
          this.maxLength !== this.loadData?.diameter;
        this.maxWidth = this.loadData?.diameter ? this.loadData.diameter : 100;
        this.maxHeight = this.loadData?.height ? this.loadData.height : 100;
        this.maxLength = this.loadData?.diameter ? this.loadData.diameter : 100;
      } else if (this.loadData.loadType === 2) {
        this.hasDimensions = !!(this.loadData?.diameter && this.loadData?.length);
        isChanged =
          this.maxWidth !== this.loadData?.diameter ||
          this.maxHeight !== this.loadData?.diameter ||
          this.maxLength !== this.loadData?.length;
        this.maxWidth = this.loadData?.diameter ? this.loadData.diameter : 100;
        this.maxHeight = this.loadData?.diameter ? this.loadData.diameter : 100;
        this.maxLength = this.loadData?.length ? this.loadData.length : 100;
      }

      if (isChanged && !changes.loadData?.firstChange) {
        this.form.controls.axisX.setValue(this.maxWidth / 2);
        this.form.controls.axisY.setValue(this.maxHeight / 2);
        this.form.controls.axisZ.setValue(this.maxLength / 2);
        this.gravityShowCenter = false;
      }
      if (this.defaultValue) {
        if (this.gravityShowCenter && this.centerOfMass) {
          this.setGravity(true);
        } else {
          this.setGravity(false);
        }
      }
    }
    if (changes.loadData && changes.loadData?.currentValue.centerOfMass) {
      const obj = changes.loadData?.currentValue.centerOfMass;
      if (!!obj.x && !!obj.y && !!obj.z) {
        this.gravityShowCenter = true;
        this.setLoadDataVis(true);
        this.form.controls.axisX.setValue(obj.x, { emitEvent: false });
        this.form.controls.axisY.setValue(obj.y, { emitEvent: false });
        this.form.controls.axisZ.setValue(obj.z, { emitEvent: false });
      }
    } else if (changes.loadData?.firstChange) {
      this.setLoadDataVis(false);
      this.gravityShowCenter = false;
      this.resetAxis();
    }
  }

  private resetAxis() {
    this.form.controls.axisX.setValue(this.maxWidth / 2, { emitEvent: false });
    this.form.controls.axisY.setValue(this.maxHeight / 2, { emitEvent: false });
    this.form.controls.axisZ.setValue(this.maxLength / 2, { emitEvent: false });
  }

  private setGravity(displayGravity = !this.gravityShowCenter): void {
    this.gravityShowCenter = displayGravity;
    if (this.gravityShowCenter) {
      this.form.enable();
    } else {
      this.form.disable();
      this.resetAxis();
    }
  }

  private setLoadDataVis(displayGravity: boolean): void {
    this.loadDataVis = {
      ...this.loadData,
      centerOfMass: displayGravity
        ? {
            x: +this.form.controls.axisX.value,
            y: +this.form.controls.axisY.value,
            z: +this.form.controls.axisZ.value,
          }
        : null,
    };
  }

  public modelChange(displayGravity = !this.gravityShowCenter): void {
    this.gravityShowCenter = displayGravity;
    this.setLoadDataVis(displayGravity);
    if (this.gravityShowCenter) {
      this.form.enable();
    } else {
      this.form.disable();
    }
    this.onSubmit();
  }

  public onSubmit() {
    if (this.form.valid && this.gravityShowCenter) {
      const centerOfMass = {
        x: +this.form.controls.axisX.value,
        y: +this.form.controls.axisY.value,
        z: +this.form.controls.axisZ.value,
      };
      this.centerOfMass.emit(centerOfMass);
      this.loadDataVis = {
        ...this.loadDataVis,
        centerOfMass: centerOfMass,
      }
    } else {
      this.centerOfMass.emit();
    }
  }

  private generateForm() {
    return new UntypedFormGroup({
      axisX: new UntypedFormControl(50),
      axisY: new UntypedFormControl(50),
      axisZ: new UntypedFormControl(50),
    });
  }
}
